import { z } from 'zod';

/**
 * Phase 3 — Agents marketplace DTOs.
 *
 * Mirror of the backend's AgentRegistryService input + AgentDefinitionMetadata
 * + AgentRunMetadata shapes.  Both sides must stay in lockstep — adding a
 * field requires touching this file AND the service AND the DB column.
 *
 * See docs/ai-platform/04-AGENTS-AND-TRUST.md.
 */

export const AgentTrustTier = z.enum(['L0', 'L1', 'L2', 'L3', 'L4', 'L5']);
export type AgentTrustTierValue = z.infer<typeof AgentTrustTier>;

export const AgentTemplateId = z.enum([
  'pm-assistant',
  'reviewer-bot',
  'triage-bot',
  'standup-buddy',
  'estimator',
  'customer-voice',
  'release-manager',
  'onboarding-concierge',
  'custom',
]);
export type AgentTemplateIdValue = z.infer<typeof AgentTemplateId>;

export const AgentRunStatus = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
  'paused',
  'cancelled',
]);
export type AgentRunStatusValue = z.infer<typeof AgentRunStatus>;

export const AgentTriggerType = z.enum(['mention', 'event', 'cron', 'manual', 'webhook']);
export type AgentTriggerTypeValue = z.infer<typeof AgentTriggerType>;

const HandleRegex = /^[a-z0-9][a-z0-9_-]{1,38}[a-z0-9]$/;

const AgentToolNameSchema = z.string().min(1).max(100).regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/);

/** Canonical storage is string[]. Legacy objects are accepted only when they
 * carry no tool-specific restrictions: unsupported restrictions must not be
 * discarded into a broader grant. An explicit [] grants no tools. */
export const AGENT_ALLOWED_TOOLS_HELP =
  'Select the tools this agent may use. No tools selected means no tool access. Existing agents with an empty list need an explicit selection.';
export const AgentAllowedToolsSchema = z.array(z.union([
  AgentToolNameSchema,
  z.object({ tool: AgentToolNameSchema, scope: z.object({}).strict().optional() }).strict(),
])).transform((entries) => [...new Set(entries.map((entry) =>
  typeof entry === 'string' ? entry : entry.tool,
))]);

/** Other scope keys remain compatible; a configured project list is strict. */
export const AgentScopeSchema = z.object({
  projects: z.array(z.string().uuid().transform((id) => id.toLowerCase())).optional(),
}).passthrough();

export const CreateAgentSchema = z.object({
  templateId: AgentTemplateId,
  name: z.string().min(1).max(120),
  handle: z.string().regex(HandleRegex, {
    message:
      "handle must be 3-40 chars, lowercase alphanumeric + dash/underscore, first/last alphanumeric (e.g. 'reviewer-bot')",
  }),
  description: z.string().max(1000).optional().nullable(),
  avatarUrl: z.string().url().max(500).optional().nullable(),
  personalityPrompt: z.string().min(1).max(16000),
  allowedModels: z.array(z.string().min(1)).min(1),
  defaultModel: z.string().min(1),
  allowedTools: AgentAllowedToolsSchema.describe(AGENT_ALLOWED_TOOLS_HELP),
  scope: AgentScopeSchema.optional(),
  triggers: z.array(z.unknown()).optional(),
  monthlyBudgetCents: z.number().int().min(0).optional(),
  maxIterations: z.number().int().min(1).max(20).optional(),
  workingHours: z.record(z.string(), z.unknown()).optional().nullable(),
  enabled: z.boolean().optional(),
});
export type CreateAgentInput = z.infer<typeof CreateAgentSchema>;

export const UpdateAgentSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(1000).optional().nullable(),
  avatarUrl: z.string().url().max(500).optional().nullable(),
  personalityPrompt: z.string().min(1).max(16000).optional(),
  allowedModels: z.array(z.string().min(1)).min(1).optional(),
  defaultModel: z.string().min(1).optional(),
  allowedTools: AgentAllowedToolsSchema.describe(AGENT_ALLOWED_TOOLS_HELP).optional(),
  scope: AgentScopeSchema.optional(),
  triggers: z.array(z.unknown()).optional(),
  monthlyBudgetCents: z.number().int().min(0).optional(),
  maxIterations: z.number().int().min(1).max(20).optional(),
  workingHours: z.record(z.string(), z.unknown()).optional().nullable(),
});
export type UpdateAgentInput = z.infer<typeof UpdateAgentSchema>;

export const SetEnabledSchema = z.object({ enabled: z.boolean() });
export type SetEnabledInput = z.infer<typeof SetEnabledSchema>;

export const AgentDefinitionSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  botUserId: z.string().uuid(),
  templateId: z.string(),
  name: z.string(),
  handle: z.string(),
  description: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  personalityPrompt: z.string(),
  allowedModels: z.array(z.string()),
  defaultModel: z.string(),
  allowedTools: z.unknown(),
  trustTier: AgentTrustTier,
  scope: z.unknown(),
  triggers: z.unknown(),
  monthlyBudgetCents: z.number().int(),
  maxIterations: z.number().int(),
  workingHours: z.unknown(),
  enabled: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentDefinitionProjection = z.infer<typeof AgentDefinitionSchema>;

export const AgentRunSchema = z.object({
  id: z.string().uuid(),
  agentDefinitionId: z.string().uuid(),
  teamId: z.string().uuid(),
  triggeredBy: z.string().uuid().nullable(),
  triggerType: AgentTriggerType,
  triggerPayload: z.unknown(),
  status: AgentRunStatus,
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  steps: z.unknown(),
  iterations: z.number().int(),
  totalCostCents: z.number().int(),
  totalTokens: z.number().int(),
  resultSummary: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  correlationId: z.string().nullable(),
  decisionTraceHash: z.string().nullable(),
  previousTraceHash: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentRunProjection = z.infer<typeof AgentRunSchema>;

export const AgentListResponseSchema = z.object({
  items: z.array(AgentDefinitionSchema),
});
export type AgentListResponse = z.infer<typeof AgentListResponseSchema>;

export const AgentRunListResponseSchema = z.object({
  items: z.array(AgentRunSchema),
});
export type AgentRunListResponse = z.infer<typeof AgentRunListResponseSchema>;
